import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileUp, Save, Clock, Receipt, Banknote, Calendar as CalendarIcon, Trash2, Plus, Calculator } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AccountingTab() {
  const [activeSubTab, setActiveSubTab] = useState("overview"); // overview, ai, manual
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Manual Form State
  const [manualForm, setManualForm] = useState({
    type: "fixed", // fixed | variable | sales
    name: "",
    amount: "",
    vat: "",
    date: new Date().toISOString().slice(0, 10),
  });

  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/accounting`);
      setExpenses(res.data.records || []);
    } catch (e) {
      console.warn("Accounting API not ready yet", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/accounting`, {
        type: manualForm.type,
        name: manualForm.name,
        amount: parseFloat(manualForm.amount) || 0,
        vat: parseFloat(manualForm.vat) || 0,
        date: manualForm.date
      });
      toast.success("Sparad!");
      setManualForm(f => ({ ...f, name: "", amount: "", vat: "" }));
      loadData();
    } catch (err) {
      toast.error("Kunde inte spara record.");
    }
  };

  const handleAIUpload = async (type) => {
    // "simulate" upload
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      toast.loading("AI analyserar bilden...");
      
      // Simulate delay
      setTimeout(() => {
        toast.dismiss();
        toast.success("AI: Analys klar!");
        // dummy payload
        api.post(`/admin/accounting`, {
          type: type === 'sale' ? 'sales' : 'variable',
          name: `AI Skannad ${type === 'sale' ? 'Försäljning' : 'Faktura'}`,
          amount: type === 'sale' ? 5000 : 2500,
          vat: type === 'sale' ? 600 : 300,
          date: new Date().toISOString().slice(0, 10)
        }).then(() => loadData());
      }, 3000);
    };
    fileInput.click();
  };

  const deleteRecord = async (id) => {
    try {
      await api.delete(`/admin/accounting/${id}`);
      loadData();
    } catch {
      toast.error("Kunde inte radera");
    }
  };

  // Date filtering and calculations
  const isWithinRange = (dateStr, start, end) => {
    const d = new Date(dateStr).getTime();
    return d >= new Date(start).getTime() && d <= new Date(end).getTime();
  };

  const stats = {
    incomingVat: 0,
    outgoingVat: 0,
    totalSales: 0,
    totalVariables: 0,
    totalFixed: 0,
  };

  expenses.filter(e => isWithinRange(e.date, dateRange.start, dateRange.end)).forEach(e => {
    const amt = parseFloat(e.amount) || 0;
    const v = parseFloat(e.vat) || 0;
    if (e.type === "sales") {
      stats.totalSales += amt;
      stats.incomingVat += v;
    } else {
      stats.outgoingVat += v;
      if (e.type === "fixed") stats.totalFixed += amt;
      if (e.type === "variable") stats.totalVariables += amt;
    }
  });

  const vatDifference = stats.incomingVat - stats.outgoingVat;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#EDEDED] tracking-tight">Beräkningar & Moms</h2>
          <p className="text-sm text-[#D4D4D8] mt-1">AI-skanning och manuell inmatning för att hantera moms, fasta/rörliga utgifter.</p>
        </div>
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="bg-[#121212] p-4 rounded-xl border border-[#27272A]">
        <TabsList className="bg-[#0A0A0A] border border-[#27272A] w-full max-w-sm grid grid-cols-3 mb-4">
          <TabsTrigger value="overview">Överblick</TabsTrigger>
          <TabsTrigger value="ai">AI Skanner</TabsTrigger>
          <TabsTrigger value="manual">Manuell</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="flex items-center gap-4 bg-[#0A0A0A] p-4 rounded-lg border border-[#27272A]">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-[#D4D4D8]" />
              <Input type="date" value={dateRange.start} onChange={e => setDateRange(r => ({...r, start: e.target.value}))} className="bg-[#121212] text-xs h-8 w-32 border-[#27272A]" />
              <span className="text-[#D4D4D8]">-</span>
              <Input type="date" value={dateRange.end} onChange={e => setDateRange(r => ({...r, end: e.target.value}))} className="bg-[#121212] text-xs h-8 w-32 border-[#27272A]" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#0A0A0A] p-5 rounded-lg border border-[#27272A]">
              <p className="text-xs text-[#D4D4D8] uppercase tracking-wider font-semibold mb-2">Ingående Moms (Försäljning)</p>
              <p className="text-3xl font-bold text-[#10B981]">{stats.incomingVat.toFixed(2)} kr</p>
            </div>
            <div className="bg-[#0A0A0A] p-5 rounded-lg border border-[#27272A]">
              <p className="text-xs text-[#D4D4D8] uppercase tracking-wider font-semibold mb-2">Utgående Moms (Inköp/Afgifter)</p>
              <p className="text-3xl font-bold text-[#EF4444]">{stats.outgoingVat.toFixed(2)} kr</p>
            </div>
            <div className={`p-5 rounded-lg border border-[#27272A] ${vatDifference > 0 ? 'bg-[#10B981]/10 border-[#10B981]/30' : vatDifference < 0 ? 'bg-[#EF4444]/10 border-[#EF4444]/30' : 'bg-[#0A0A0A]'}`}>
              <p className="text-xs text-[#D4D4D8] uppercase tracking-wider font-semibold mb-2">Momsskillnad (Att betala/få)</p>
              <p className={`text-3xl font-bold ${vatDifference > 0 ? 'text-[#10B981]' : vatDifference < 0 ? 'text-[#EF4444]' : 'text-white'}`}>
                {vatDifference > 0 ? '+' : ''}{vatDifference.toFixed(2)} kr
              </p>
            </div>
          </div>

          <div className="bg-[#0A0A0A] p-5 rounded-lg border border-[#27272A] mt-4">
            <h3 className="font-semibold text-[#EDEDED] mb-4">Total Sammanställning</h3>
            <div className="space-y-3">
              <div className="flex justify-between border-b border-[#27272A] pb-2">
                <span className="text-[#D4D4D8]">Total Försäljning (Exkl moms)</span>
                <span className="font-medium text-[#EDEDED]">{stats.totalSales.toFixed(2)} kr</span>
              </div>
              <div className="flex justify-between border-b border-[#27272A] pb-2">
                <span className="text-[#D4D4D8]">Rörliga Utgifter (Inköp varor)</span>
                <span className="font-medium text-[#EF4444]">{stats.totalVariables.toFixed(2)} kr</span>
              </div>
              <div className="flex justify-between border-b border-[#27272A] pb-2">
                <span className="text-[#D4D4D8]">Fasta Avgifter (Hyra, el)</span>
                <span className="font-medium text-[#EF4444]">{stats.totalFixed.toFixed(2)} kr</span>
              </div>
              <div className="flex justify-between pt-2">
                <span className="font-bold text-[#EDEDED]">Beräknad Vinst</span>
                <span className={`font-bold ${(stats.totalSales - stats.totalVariables - stats.totalFixed) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {(stats.totalSales - stats.totalVariables - stats.totalFixed).toFixed(2)} kr
                </span>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ai" className="space-y-6 flex flex-col items-center py-10">
          <div className="text-center max-w-md">
            <Calculator className="w-12 h-12 text-[#D4B483] mx-auto mb-4" />
            <h3 className="text-lg font-bold text-[#EDEDED] mb-2">Låt AI Beräkna Momssen</h3>
            <p className="text-sm text-[#D4D4D8] mb-8">Ladda upp en bild på din senaste Zettle/försäljningsrapport, eller ladda upp en faktura från t.ex. Martin Servera. AI:n extraherar moms automatiskt.</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full max-w-2xl">
            <div className="border-2 border-dashed border-[#27272A] bg-[#0A0A0A] p-8 text-center rounded-xl hover:border-[#D4B483] transition-colors cursor-pointer" onClick={() => handleAIUpload('purchase')}>
              <Receipt className="w-10 h-10 text-[#D4D4D8] mx-auto mb-4" />
              <h4 className="font-semibold text-[#EDEDED] mb-1">Inköp & Fakturor</h4>
              <p className="text-xs text-[#B4B4BC]">Ladda upp bild på inköpskvitto</p>
              <Button className="mt-4 bg-[#27272A] hover:bg-[#3F3F46] text-white w-full"><FileUp className="w-4 h-4 mr-2" /> Ladda upp Inköp</Button>
            </div>

            <div className="border-2 border-dashed border-[#27272A] bg-[#0A0A0A] p-8 text-center rounded-xl hover:border-[#D4B483] transition-colors cursor-pointer" onClick={() => handleAIUpload('sale')}>
              <Banknote className="w-10 h-10 text-[#D4D4D8] mx-auto mb-4" />
              <h4 className="font-semibold text-[#EDEDED] mb-1">Dagens Försäljning</h4>
              <p className="text-xs text-[#B4B4BC]">Ladda upp rapport från kassan</p>
              <Button className="mt-4 bg-[#27272A] hover:bg-[#3F3F46] text-white w-full"><FileUp className="w-4 h-4 mr-2" /> Ladda upp Försäljning</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="manual" className="space-y-4">
          <form onSubmit={handleManualSubmit} className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-5 mb-6">
            <h3 className="font-semibold text-sm mb-4">Lägg till manuell inkomst / utgift</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div>
                <label className="text-xs text-[#D4D4D8] mb-1 block">Tid</label>
                <Input type="date" required value={manualForm.date} onChange={e => setManualForm(f => ({...f, date: e.target.value}))} className="bg-[#121212] border-[#27272A]" />
              </div>
              <div>
                <label className="text-xs text-[#D4D4D8] mb-1 block">Typ</label>
                <select value={manualForm.type} onChange={e => setManualForm(f => ({...f, type: e.target.value}))} className="w-full bg-[#121212] border border-[#27272A] text-white text-sm rounded-md h-9 px-2">
                  <option value="sales">Försäljning</option>
                  <option value="variable">Rörlig (Inköp varor)</option>
                  <option value="fixed">Fast (Hyra, El)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[#D4D4D8] mb-1 block">Benämning / Sektion</label>
                <Input required value={manualForm.name} onChange={e => setManualForm(f => ({...f, name: e.target.value}))} placeholder="t.ex Hyra" className="bg-[#121212] border-[#27272A]" />
              </div>
              <div>
                <label className="text-xs text-[#D4D4D8] mb-1 block">Belopp (exkl moms)</label>
                <Input type="number" required step="0.01" value={manualForm.amount} onChange={e => setManualForm(f => ({...f, amount: e.target.value}))} placeholder="2500" className="bg-[#121212] border-[#27272A]" />
              </div>
              <div>
                <label className="text-xs text-[#D4D4D8] mb-1 block">Moms (kr)</label>
                <Input type="number" required step="0.01" value={manualForm.vat} onChange={e => setManualForm(f => ({...f, vat: e.target.value}))} placeholder="625" className="bg-[#121212] border-[#27272A]" />
              </div>
            </div>
            <Button type="submit" className="mt-4 bg-[#D4B483] text-[#0A0A0A] hover:bg-[#b0966a]">
              <Save className="w-4 h-4 mr-2" /> Spara
            </Button>
          </form>

          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-0 overflow-hidden">
            <table className="w-full text-left text-sm text-[#D4D4D8]">
              <thead className="bg-[#121212] text-xs uppercase text-[#B4B4BC] border-b border-[#27272A]">
                <tr>
                  <th className="px-4 py-3">Datum</th>
                  <th className="px-4 py-3">Typ</th>
                  <th className="px-4 py-3">Benämning</th>
                  <th className="px-4 py-3">Belopp</th>
                  <th className="px-4 py-3">Moms</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((record) => (
                  <tr key={record.id || record._id} className="border-b border-[#27272A]/50 last:border-0 hover:bg-[#1A1A1A]">
                    <td className="px-4 py-3">{(record.date || '').slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      {record.type === 'sales' ? <span className="text-[#10B981]">Försäljning</span> :
                       record.type === 'fixed' ? <span className="text-[#EF4444]">Fast</span> : 
                       <span className="text-[#F59E0B]">Rörlig</span>}
                    </td>
                    <td className="px-4 py-3 text-white">{record.name}</td>
                    <td className="px-4 py-3">{record.amount} kr</td>
                    <td className="px-4 py-3">{record.vat} kr</td>
                    <td className="px-4 py-3">
                      <Button variant="ghost" size="sm" onClick={() => deleteRecord(record.id)} className="h-8 w-8 p-0 text-[#EF4444] hover:bg-[#EF4444]/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {expenses.length === 0 && (
                  <tr><td colSpan="6" className="text-center py-6">Inga poster registrerade ännu.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}